import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, rad, sp } from "../../constants/styles";
import ReceiptView from "../landlord/ReceiptView";
import TxnHistory from "../landlord/TxnHistory";

function TenPayHist() {
  const { t, h, pays, props, tens, currUserId } = useContext(Ctx);
  const ct = currUserId;
  const mp = pays.filter(x => x.tid === ct);
  const allTxns = mp.flatMap(p => (p.txns || []).map(tx => ({ ...tx, parentMo: p.mo })));
  const [receiptPay, setReceiptPay] = useState(null);
  const [tab, setTab] = useState("summary"); // "summary" | "txns"
  const me = tens.find(x => x.id === ct);
  const myProp = props.find(x => x.id === me?.pid);

  if (receiptPay) return <ReceiptView pay={receiptPay} prop={myProp} tenant={me} onBack={() => setReceiptPay(null)} />;

  const paidTotal = mp.filter(p => p.st === "paid").reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const advanceTotal = mp.reduce((s, p) => s + (p.advance || 0), 0);
  const dueTotal = mp.filter(p => (p.st === "due" || p.st === "partial") && !p.pending).reduce((s, p) => {
    const gross = p.amt + (p.com || 0);
    const txnPaid = (p.txns || []).reduce((sum, tx) => sum + (tx.status === "verified" ? tx.amt : 0), 0);
    return s + Math.max(0, gross - txnPaid);
  }, 0);
  const pendingCount = mp.filter(p => p.pending).length;

  return <div style={{ paddingTop: 16 }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{t.myPay}</h2>

    {/* Summary strip */}
    <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: "45%", background: `${h.gn}10`, border: `1px solid ${h.gn}20`, borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>{t.paid}</div>
        <div style={{ color: h.gn, fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{paidTotal.toLocaleString()}</div>
      </div>
      <div style={{ flex: 1, minWidth: "45%", background: `${h.rd}10`, border: `1px solid ${h.rd}20`, borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>{t.due}</div>
        <div style={{ color: h.rd, fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{dueTotal.toLocaleString()}{pendingCount > 0 ? <span style={{ fontSize: 10, color: h.wn, fontWeight: 500 }}> +{pendingCount} ⏳</span> : null}</div>
      </div>
      {advanceTotal > 0 && <div style={{ flex: 1, minWidth: "45%", background: `${h.ac}10`, border: `1px solid ${h.ac}20`, borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>Advance</div>
        <div style={{ color: h.ac, fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{advanceTotal.toLocaleString()}</div>
      </div>}
    </div>

    {/* Tabs */}
    <div style={{ display: "flex", background: h.ib, borderRadius: rad.md, padding: 3, marginBottom: 14 }}>
      {[["summary", t.monthlySummary || "Monthly Summary"], ["txns", `${t.txnHistory || "Transactions"}${allTxns.length > 0 ? ` (${allTxns.length})` : ""}`]].map(([id, label]) => (
        <button key={id} onClick={() => setTab(id)} style={{
          flex: 1, padding: "8px 4px", borderRadius: rad.sm + 2, border: "none",
          background: tab === id ? h.cd : "transparent",
          color: tab === id ? h.ac : h.ts,
          fontSize: 12, fontWeight: tab === id ? 700 : 500,
          cursor: "pointer", transition: "all 0.2s",
          boxShadow: tab === id ? h.sh : "none",
        }}>{label}</button>
      ))}
    </div>

    {/* Monthly Summary tab */}
    {tab === "summary" && <>
      {mp.length === 0 && <p style={{ color: h.sc, textAlign: "center", padding: 24 }}>{t.noData}</p>}
      {mp.slice().reverse().map(p => {
        const gross = p.amt + (p.com || 0);
        const txnPaid = (p.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0);
        const remaining = Math.max(0, gross - txnPaid);
        const isPartial = p.st === "partial" || (txnPaid > 0 && txnPaid < gross && p.st !== "paid");
        const hasAdvance = (p.advance || 0) > 0;
        const isAdvanceOnly = gross === 0 && hasAdvance;
        const displayAmt = isAdvanceOnly ? p.advance : isPartial ? remaining : gross;
        const stColor = p.st === "paid" ? h.gn : isPartial ? "#f59e0b" : p.pending ? h.wn : h.rd;
        const stLabel = isAdvanceOnly ? `💰 Advance`
          : p.st === "paid" ? (hasAdvance ? `✓ Paid + Advance` : "✓ " + t.paid)
          : isPartial ? `⚡ Partial — ₹${remaining.toLocaleString()} due`
          : p.pending ? "⏳ " + t.pending : t.due;
        return <div key={p.id} style={{ ...crd(h), borderLeft: isAdvanceOnly ? `3px solid ${h.ac}` : undefined }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{isAdvanceOnly ? "💰 Advance Payment" : p.mo}</div>
              <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{p.dt || "—"} · {(p.mth || "—").toUpperCase()}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: isAdvanceOnly ? h.ac : h.tx, fontWeight: 700, fontSize: 15 }}>₹{displayAmt.toLocaleString()}</div>
              <span style={bdg(`${isAdvanceOnly ? h.ac : stColor}20`, isAdvanceOnly ? h.ac : stColor)}>{stLabel}</span>
            </div>
          </div>
          {p.com > 0 && <div style={{ color: h.sc, fontSize: 10, marginTop: 4 }}>{t.rentAmt} ₹{p.amt.toLocaleString()} + {t.commonAmt} ₹{p.com}</div>}
          {isPartial && txnPaid > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", background: "rgba(22,163,74,0.06)", borderRadius: 8, border: "1px solid rgba(22,163,74,0.15)" }}>
            <span style={{ fontSize: 12 }}>✅</span>
            <span style={{ color: "#16a34a", fontSize: 11, fontWeight: 600 }}>Paid: ₹{txnPaid.toLocaleString()}</span>
            <span style={{ color: h.sc, fontSize: 10, marginLeft: "auto" }}>of ₹{gross.toLocaleString()}</span>
          </div>}
          {hasAdvance && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", background: `${h.ac}08`, borderRadius: 8, border: `1px solid ${h.ac}20` }}>
            <span style={{ fontSize: 12 }}>💰</span>
            <span style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>Advance: ₹{p.advance.toLocaleString()}</span>
          </div>}
          {p.st === "paid" && <button onClick={() => setReceiptPay(p)} style={{ marginTop: 10, width: "100%", padding: "10px", borderRadius: 10, border: `1px solid ${h.ac}`, background: `${h.ac}08`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{"🧾 " + t.receiptDownShare}</button>}
          {p.txns && p.txns.length > 0 && <TxnHistory txns={p.txns} payment={p} />}
        </div>;
      })}
    </>}

    {/* Transactions tab */}
    {tab === "txns" && <>
      {allTxns.length === 0 && <p style={{ color: h.sc, textAlign: "center", padding: 24 }}>{t.noData}</p>}
      {allTxns.slice().reverse().map((tx, i) => {
        const stColor = { approved: h.gn, verified: h.gn, pending: h.wn, rejected: h.rd };
        const stLabel = { approved: "✓ " + t.approved, verified: "✓ Verified", pending: "⏳ " + t.pending, rejected: "✕ " + t.rejected };
        return <div key={tx.id || i} style={{ ...crd(h), borderLeft: `3px solid ${stColor[tx.status] || h.sc}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div>
              <div style={{ color: h.tx, fontWeight: 700, fontSize: 15 }}>₹{tx.amt.toLocaleString()}</div>
              <div style={{ color: h.sc, fontSize: 11 }}>{tx.parentMo}</div>
            </div>
            <span style={bdg(`${stColor[tx.status] || h.sc}20`, stColor[tx.status] || h.sc)}>{stLabel[tx.status] || tx.status}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            <div style={{ color: h.sc, fontSize: 11 }}>{t.method + ":"} <span style={{ color: h.tx, fontWeight: 500 }}>{(tx.mth || "—").toUpperCase()}</span></div>
            <div style={{ color: h.sc, fontSize: 11 }}>{t.date + ":"} <span style={{ color: h.tx, fontWeight: 500 }}>{tx.dt}</span></div>
            <div style={{ color: h.sc, fontSize: 11 }}>{t.type + ":"} <span style={{ color: tx.type === "full" ? h.gn : tx.type === "advance" ? h.ac : h.wn, fontWeight: 600 }}>{tx.type === "full" ? t.full : tx.type === "advance" ? (t.advancePay || "Advance") : t.partial}</span></div>
            {tx.status === "approved" && tx.remaining > 0 && <div style={{ color: h.sc, fontSize: 11 }}>{t.remaining + ":"} <span style={{ color: h.rd, fontWeight: 600 }}>₹{tx.remaining.toLocaleString()}</span></div>}
            {tx.status === "approved" && tx.approvedAt && <div style={{ color: h.sc, fontSize: 11 }}>{t.approvedOn + ":"} <span style={{ color: h.gn }}>{tx.approvedAt}</span></div>}
            {tx.status === "rejected" && <div style={{ color: h.sc, fontSize: 11 }}>{t.rejectedOn + ":"} <span style={{ color: h.rd }}>{tx.rejectedAt}</span></div>}
          </div>
          {tx.totalWas > 0 && <div style={{ marginTop: 6, color: h.sc, fontSize: 10 }}>{t.totalWas} ₹{tx.totalWas.toLocaleString()}</div>}
        </div>;
      })}
    </>}
  </div>;
}

export default TenPayHist;
